import FormLoader from 'components/FormLoader/FormLoader.component';
import OTPInput from 'components/Otp/Otp.component';
import {
  useGetOtpData,
  useIsNoteVisible,
  useResetOtpStateOnUnmount,
} from 'components/OtpModal/OtpModal.hooks';
import {
  BottomBoxOtp,
  CentralBoxOtp,
  ContainerModalOtp,
  FlexOtp,
  FormOtp,
  NotePageOtp,
  VerifySubtitle,
} from 'components/OtpModal/partials/style/OtpModalBody.style';
import { SubmitButtonOtp } from 'components/OtpModalPartials';
import { Formik } from 'formik';
import SuccessOtpImg from 'images/otp/success.svg';
import { useEffect, useRef, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { useDispatch, useSelector } from 'react-redux';
import { selectOtpErrors } from 'services/otp/otp.selectors';
import { selectUserProfile } from 'services/user/user.selectors';
import colors from 'style-utils/colors';
import { ColWithPadding } from 'style/colCustomized.style';
import { getHiddenPhoneNumber } from 'utils/functions/formatPhoneNumber';
import { useCreateHandleContractOtpCheckSubmit } from './OtpContractModal.hooks';
import RefreshOtpLabel from './RefreshOtpLabel.component';

export type OtpContractModalBodyProps = {
  onSuccess: () => Promise<void>;
};

export default function OtpContractModalBody({
  onSuccess,
}: OtpContractModalBodyProps) {
  const hideShowMessageTimoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  const { initialValues, validate } = useGetOtpData();
  const dispatch = useDispatch();

  const { hasError, counterRefresh, hintText } = useSelector(selectOtpErrors);
  const isNoteVisible = useIsNoteVisible(counterRefresh >= 3);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const {
    accessData: { phonePrefix, phoneNumber },
  } = useSelector(selectUserProfile);

  const inputRef = useRef<HTMLInputElement>(null);

  const onSuccessfullyCheckedOtp = () => {
    setShowSuccessMessage(true);
    onSuccess();

    hideShowMessageTimoutIdRef.current = setTimeout(() => {
      setShowSuccessMessage(false);
    }, 1500);
  };

  const handleSubmit = useCreateHandleContractOtpCheckSubmit(
    { phonePrefix, phoneNumber },
    onSuccessfullyCheckedOtp,
  );

  useResetOtpStateOnUnmount();

  useEffect(() => {
    return () => {
      clearTimeout(hideShowMessageTimoutIdRef.current);
    };
  }, []);

  return (
    <ContainerModalOtp id="OtpContractModal">
      {showSuccessMessage && (
        <img src={SuccessOtpImg} alt="OTP code is correct" />
      )}
      <Formik
        initialValues={initialValues}
        validate={validate}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={handleSubmit}
      >
        <FormOtp>
          <FormLoader overlayViewMode="fullscreen" viewMode="absolute" />
          <CentralBoxOtp>
            <VerifySubtitle>
              <Translate
                id="text.modifyPassword.modal.step1.sms"
                data={{
                  maskedContact: getHiddenPhoneNumber({ phoneNumber }),
                }}
              />
            </VerifySubtitle>
            <FlexOtp>
              <OTPInput autoFocus isNumberInput length={4} />
              {isNoteVisible ? (
                <ColWithPadding padding="16px 0 8px">
                  <NotePageOtp
                    textColor={colors.blackTwelve}
                    borderSize="2px"
                    borderColor={colors.yellowNote}
                    containerPadding="0 0 0 8px"
                    size="12px"
                    lineHeight="14px"
                    noNote
                    text="text.otp.otpRefreshBlock"
                  />
                </ColWithPadding>
              ) : (
                <RefreshOtpLabel />
              )}
            </FlexOtp>
          </CentralBoxOtp>
          <BottomBoxOtp fluid justify="center">
            <Translate>
              {({ translate }) => (
                <SubmitButtonOtp
                  shouldValidateForm={false}
                  enableDisabledOnPristine={true}
                >
                  {translate('lbl.verify')}
                </SubmitButtonOtp>
              )}
            </Translate>
          </BottomBoxOtp>
        </FormOtp>
      </Formik>
    </ContainerModalOtp>
  );
}
